// @ts-nocheck
import React, { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { HelpCircle, LayoutDashboard, Archive, Settings, LogOut, FileClock } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useCompanyContext } from '../context/CompanyContext';
import { useClientWorkspaceCompanyCard } from '../hooks/useClientWorkspaceCompanyCard';
import AppSidebar, { FirmSidebarLogoHeader, SidebarItem } from '../../shell/AppSidebar';
import AppPageHeader from '../../shell/AppPageHeader';
import { AppShell, AppShellBody, AppShellTopRow } from '../../shell/AppShell';
import { shellMainPaddingX } from '../../../utils/shellLayoutClasses';
import { openAppHelp } from '../../../utils/appHelp';

export default function ClientLayout() {
  const { logout } = useAuth();
  const { currentAnalysis, archivedAnalyses, refreshAnalysesList } = useCompanyContext();
  /** Workspace company name + closing/status/law-firm meta — drives the compound page title and slim meta strip. */
  const { showDealContextCard, companySummaryProps } = useClientWorkspaceCompanyCard(archivedAnalyses);
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;
  const isHome = path === '/client/analyses';
  const isArchives = path === '/client/archives';
  const isSettings = path === '/client/settings';
  const isRunHistory = path === '/client/run-history' || path.startsWith('/client/run-history/');
  /** `/client/{uuid}` — workflow overview; shell title is “280G Analysis”, analysis name lives in WorkflowHomePage. */
  const isClientOverviewByAnalysisId = /^\/client\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    path
  );
  /** Closing date moves into page summary badges on workflow overview, analyses list, and run history. */
  const hideShellClosingInPageBadges =
    isClientOverviewByAnalysisId || path === '/client/run-history' || path === '/client/analyses';
  /** Client portal: start with nav icons-only; user expands for full labels. */
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);

  return (
    <AppShell>
      <AppShellTopRow
        logo={
          <FirmSidebarLogoHeader
            collapsed={sidebarCollapsed}
            onToggleCollapsed={() => setSidebarCollapsed((v) => !v)}
          />
        }
        header={
          <AppPageHeader
            variant="firmShell"
            title={
              isHome
                ? '280G Analyses'
                : isArchives
                  ? 'Archived Analyses'
                  : isSettings
                    ? 'Settings'
                    : isRunHistory
                      ? 'Analysis Run History'
                      : isClientOverviewByAnalysisId
                        ? '280G Analysis'
                        : currentAnalysis?.name || '280G Analysis'
            }
            clientCompanyName={showDealContextCard ? companySummaryProps.companyName : undefined}
            clientMeta={
              showDealContextCard
                ? {
                    closingDate: hideShellClosingInPageBadges ? undefined : companySummaryProps.closingDate,
                    workStatus: companySummaryProps.workStatus,
                    lawFirmName: companySummaryProps.lawFirmName,
                  }
                : undefined
            }
          />
        }
      />
      <AppShellBody
        sidebar={
          <AppSidebar
            collapsed={sidebarCollapsed}
            onToggleCollapsed={() => setSidebarCollapsed((v) => !v)}
            omitLogoHeader
            footer={
              <>
                <SidebarItem icon={HelpCircle} label="Help" onClick={openAppHelp} collapsed={sidebarCollapsed} />
                <SidebarItem
                  icon={Settings}
                  label="Settings"
                  active={isSettings}
                  onClick={() => navigate('/client/settings')}
                  collapsed={sidebarCollapsed}
                />
                <SidebarItem
                  icon={LogOut}
                  label="Sign out"
                  variant="danger"
                  onClick={logout}
                  collapsed={sidebarCollapsed}
                />
              </>
            }
          >
            <SidebarItem
              icon={LayoutDashboard}
              label="280G Analyses"
              active={isHome}
              onClick={async () => {
                await refreshAnalysesList();
                navigate('/client/analyses');
              }}
              collapsed={sidebarCollapsed}
            />
            <SidebarItem
              icon={FileClock}
              label="Analysis Run History"
              active={isRunHistory}
              onClick={() => navigate('/client/run-history')}
              collapsed={sidebarCollapsed}
            />
            <SidebarItem
              icon={Archive}
              label="Archived Analyses"
              active={isArchives}
              onClick={() => navigate('/client/archives')}
              collapsed={sidebarCollapsed}
            />
          </AppSidebar>
        }
      >
        <main className="flex min-h-0 min-w-0 flex-1 flex-col">
          <div className={`flex-1 overflow-auto pb-6 pt-3 sm:pt-4 ${shellMainPaddingX}`}>
            <div className="w-full">
              <Outlet />
            </div>
          </div>
        </main>
      </AppShellBody>
    </AppShell>
  );
}
